<template>
  <div class="waw-triage">
    <p v-if="readOnly" class="waw-locked">
      These answers are saved and can&rsquo;t be changed here. If something is wrong, contact Teamio
      and we&rsquo;ll sort it out with you.
    </p>

    <!-- Q1 — the branch everything else hangs off. -->
    <fieldset class="waw-q">
      <legend class="waw-q__label">
        Do your patients already have a phone number they message you on for WhatsApp?
      </legend>
      <div class="waw-choices">
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': answers.hasWhatsAppNumber === true }"
          :aria-pressed="answers.hasWhatsAppNumber === true ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-has-number-yes"
          @click="setHasNumber(true)"
        >
          <span class="waw-choice__title">Yes, we have a number</span>
          <span class="waw-choice__sub">A number patients already use, or one we own</span>
        </button>
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': answers.hasWhatsAppNumber === false }"
          :aria-pressed="answers.hasWhatsAppNumber === false ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-has-number-no"
          @click="setHasNumber(false)"
        >
          <span class="waw-choice__title">No, we need one</span>
          <span class="waw-choice__sub">We&rsquo;ll get you a number for WhatsApp</span>
        </button>
      </div>
    </fieldset>

    <!-- Q2 — only asked when Q1 is yes. Without it the branch is undecidable: moving a number
         that is live in the WhatsApp app and buying a new one are opposite outcomes. The two "yes"
         answers are the move-or-keep choice (keepsNumberOnApp), asked in the same breath so a
         practice that is on the app can say "and we're keeping it there" — see the script comment. -->
    <fieldset v-if="answers.hasWhatsAppNumber === true" class="waw-q">
      <legend class="waw-q__label">
        Is that number set up in the WhatsApp app on a phone right now?
      </legend>
      <div class="waw-choices waw-choices--three">
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': isMove }"
          :aria-pressed="isMove ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-number-on-app-yes"
          @click="patch({ numberOnApp: true, keepsNumberOnApp: false })"
        >
          <span class="waw-choice__title">Yes &mdash; move it to Teamio</span>
          <span class="waw-choice__sub">Your team uses that number in Teamio instead of the app</span>
        </button>
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': isKeep }"
          :aria-pressed="isKeep ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-number-on-app-keep"
          @click="patch({ numberOnApp: true, keepsNumberOnApp: true })"
        >
          <span class="waw-choice__title">Yes &mdash; keep it on the app</span>
          <span class="waw-choice__sub">Teamio gets a new number of its own</span>
        </button>
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': answers.numberOnApp === false }"
          :aria-pressed="answers.numberOnApp === false ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-number-on-app-no"
          @click="patch({ numberOnApp: false, keepsNumberOnApp: null })"
        >
          <span class="waw-choice__title">No, it&rsquo;s not</span>
          <span class="waw-choice__sub">It&rsquo;s a normal phone line, not a WhatsApp account</span>
        </button>
      </div>
      <p v-if="isMove" class="waw-branch-note">
        A number that is already live in the WhatsApp app has to be moved across carefully, so our
        team does that part with you rather than the wizard doing it automatically.
      </p>
      <p v-else-if="isKeep" class="waw-branch-note" data-test="waw-triage-keep-note">
        Your number and the WhatsApp app stay exactly as they are, and Teamio gets a new number of
        its own. WhatsApp&rsquo;s reply rule still applies to the new number: your team can reply
        freely only within 24 hours of a patient&rsquo;s last message, and after that only
        pre-approved messages, like appointment reminders, can go out. Keep using the app on your
        current number whenever you need to reach a patient first.
      </p>

      <!-- Collected HERE, not later: these answers are submitted once, and the branch that needs
           the number is the one a person picks up afterwards. See the script comment. -->
      <label class="waw-field">
        <span class="waw-field__label">What is that number?</span>
        <input
          type="tel"
          inputmode="tel"
          class="waw-input"
          :value="answers.byoNumber || ''"
          :disabled="readOnly"
          placeholder="e.g. (201) 555-0147"
          data-test="waw-triage-byo-number"
          @input="patch({ byoNumber: $event.target.value })"
        >
        <span v-if="isKeep" class="waw-field__hint">
          So our team knows which number stays on the app &mdash; nothing is sent to it.
        </span>
        <span v-else class="waw-field__hint">
          The number patients would message. We only use it to set WhatsApp up &mdash; nothing is
          sent to it.
        </span>
      </label>
    </fieldset>

    <!-- Q3 — Facebook. Copy and routing only: the sign-in itself creates whatever is missing. -->
    <fieldset class="waw-q">
      <legend class="waw-q__label">
        Who looks after your practice&rsquo;s Facebook account?
      </legend>
      <div class="waw-choices waw-choices--three">
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': answers.facebookAccess === 'self' }"
          :aria-pressed="answers.facebookAccess === 'self' ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-facebook-self"
          @click="patch({ facebookAccess: 'self' })"
        >
          <span class="waw-choice__title">I can sign in</span>
          <span class="waw-choice__sub">I have the login, or I can get it</span>
        </button>
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': answers.facebookAccess === 'other' }"
          :aria-pressed="answers.facebookAccess === 'other' ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-facebook-other"
          @click="patch({ facebookAccess: 'other' })"
        >
          <span class="waw-choice__title">Someone else does</span>
          <span class="waw-choice__sub">A marketing company or an outside person</span>
        </button>
        <button
          type="button"
          class="waw-choice"
          :class="{ 'is-on': answers.facebookAccess === 'none' }"
          :aria-pressed="answers.facebookAccess === 'none' ? 'true' : 'false'"
          :disabled="readOnly"
          data-test="waw-triage-facebook-none"
          @click="patch({ facebookAccess: 'none' })"
        >
          <span class="waw-choice__title">We don&rsquo;t have one</span>
          <span class="waw-choice__sub">We&rsquo;ve never set up Facebook for the practice</span>
        </button>
      </div>
      <!-- Kept honest with what the connect screen actually offers: since it can now mint a link,
           "be the one at this screen" would overstate what we require of them. -->
      <p v-if="answers.facebookAccess === 'other'" class="waw-branch-note">
        That&rsquo;s fine. WhatsApp requires the sign-in to come from whoever holds the Facebook
        login, so they&rsquo;ll need to complete that step themselves &mdash; either with you now,
        or from a link you can send them later.
      </p>
      <p v-else-if="answers.facebookAccess === 'none'" class="waw-branch-note">
        That&rsquo;s fine too. The sign-in step creates what&rsquo;s missing along the way &mdash;
        you&rsquo;ll just need a personal Facebook login to start it from.
      </p>
    </fieldset>
  </div>
</template>

<script>
/**
 * Wizard screen 2 — the number and Facebook questions that decide every later branch.
 *
 * ── WHAT IS DELIBERATELY *NOT* ASKED HERE: `wantsLocalNumber` ─────────────────────────────────────
 * The backend's triage contract carries a `wantsLocalNumber` answer, and the design describes
 * offering "a local number in your country" only when Twilio actually sells one there. That
 * availability is not knowable on this screen: the country gate is evaluated SERVER-side, during
 * the my-triage POST, and `my-status` returns `countryGate: null` for a practice that has not
 * posted yet. Asking "local or US?" here would mean asking before we know either the country or
 * whether a local number exists — and answering "local" routes to a human handoff, so a wrong
 * guess costs a practice the automated path for no reason.
 *
 * So this screen submits `wantsLocalNumber: null` ("the question did not apply" — the backend's
 * `normalizeTriageAnswers` models exactly that), which leaves the practice on the US purchase
 * branch, and the choice is offered on the NUMBER screen where `countryGate.localAvailable` is
 * known: "I'd rather have a local number" routes to the handoff screen, whose take-over button is a
 * `my-handoff` with reason `local_number`. (Built 2026-09-02 — WizardNumberStep / WizardHandoffStep.)
 *
 * ── WHY THE NUMBER ITSELF IS COLLECTED ON *THIS* SCREEN ──────────────────────────────────────────
 * `byoNumber` is part of the triage answer contract, and triage is submitted exactly ONCE per site.
 * The branch that actually needs the number — a number already live in the WhatsApp app — is a
 * human handoff, and the handoff Slack renders every triage answer so an operator can act from
 * Slack alone. If the number were left for the handoff screen to ask for, it could never be
 * captured at all: by then my-triage is closed and the answers are frozen. So it is asked here, the
 * moment the practice says they have one.
 *
 * It is REQUIRED (see `isWizardStepValid`) even though the backend accepts it as null. That is
 * deliberate and it is the safe direction of strictness — the frontend can refuse things the server
 * would accept, never the reverse. A handoff that pages our team saying "they have a number" and
 * does not say WHICH number is a handoff nobody can act on.
 *
 * ── KEEP OR MOVE: THE TWO "YES" ANSWERS ON Q2 (keepsNumberOnApp, 2026-09-11) ────────────────────
 * "Is that number on the WhatsApp app?" used to have one yes, and it meant MOVE (bring-your-own):
 * the app stops working on that number, chat history stays behind, and the 24-hour reply rule
 * governs a number the team used to write from freely. A practice whose staff rely on the app to
 * reach patients first can reasonably want the opposite — keep the number on the app, and give
 * Teamio a NEW number of its own. The first real non-US practice chose exactly that, and answering
 * the questions honestly would have sent it to the six move-your-number acknowledgements and a
 * purchase the server refuses. So "yes" is split: move (`keepsNumberOnApp: false` → BYO) or keep
 * (`true` → the US purchase path, no acknowledgements). Each button sets BOTH fields, so a click can
 * never leave the pair half-answered; "no" clears the choice. The keep note restates the reply
 * rule, because it still applies to the new number — only the app on the old number escapes it.
 *
 * ── READ-ONLY AFTER SUBMISSION ───────────────────────────────────────────────────────────────────
 * my-triage accepts exactly one POST per site. Coming back here after that is a REVIEW, so the
 * controls are disabled rather than silently accepting edits that could never be saved.
 */
export default {
  name: 'WizardTriageStep',

  props: {
    /** The draft answers the shell owns. Seeded from the server's echo once triage is committed. */
    answers: { type: Object, required: true },
    /** True once the answers are saved server-side — from then on this screen is a review. */
    readOnly: { type: Boolean, default: false },
  },

  computed: {
    /**
     * "Yes — move it to Teamio". Mirrors the backend's reading exactly: on the app and NOT a strict
     * keep. So a review of an answer set saved before keepsNumberOnApp existed (no key at all) shows
     * the move it always meant, rather than a question with nothing selected.
     */
    isMove() {
      return this.answers.numberOnApp === true && this.answers.keepsNumberOnApp !== true;
    },
    /** "Yes — keep it on the app": the number stays on the app and Teamio gets a new one. */
    isKeep() {
      return this.answers.numberOnApp === true && this.answers.keepsNumberOnApp === true;
    },
  },

  methods: {
    patch(fields) {
      if (this.readOnly) return;
      this.$emit('update', fields);
    },

    /**
     * Q1 also CLEARS the answers that only exist underneath it when it becomes "no".
     *
     * Without this, a practice that answers yes → "it's on WhatsApp", then changes its mind to
     * "no, we need a number", carries a stale `numberOnApp: true` into the submission — and that
     * pair routes to the bring-your-own handoff instead of the purchase they just asked for. The
     * number they typed goes the same way: it would be stored against a practice that just told us
     * it has no number. The move-or-keep choice lives under Q2, so it is cleared with it.
     */
    setHasNumber(value) {
      if (this.readOnly) return;
      this.$emit('update', value === true
        ? { hasWhatsAppNumber: true }
        : { hasWhatsAppNumber: false, numberOnApp: null, keepsNumberOnApp: null, byoNumber: null });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-locked {
  margin: 0 0 1.2rem;
  padding: 0.6rem 0.8rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  color: $pp-text-muted;
  font-size: 0.83rem;
  line-height: 1.6;
}

.waw-q {
  border: 0;
  padding: 0;
  margin: 0 0 1.6rem;

  &:last-child { margin-bottom: 0; }
}

.waw-q__label {
  font-size: 0.92rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin-bottom: 0.7rem;
  line-height: 1.5;
  float: none; /* Bootstrap floats <legend>; that breaks the grid below it. */
  width: auto;
}

.waw-choices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
  max-width: 720px;
}

.waw-choices--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.waw-choice {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: left;
  padding: 0.8rem 0.9rem;
  border-radius: $pp-radius-sm;
  border: 1px solid $pp-border;
  background: $pp-surface;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: $pp-brand-border;
    background: $pp-brand-subtle;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  &.is-on {
    border-color: $pp-brand;
    background: $pp-brand-subtle;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.1);
  }
}

.waw-choice__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: $pp-text-strong;
}

.waw-choice__sub {
  font-size: 0.78rem;
  line-height: 1.45;
  color: $pp-text-muted;
}

.waw-branch-note {
  margin: 0.7rem 0 0;
  color: $pp-text-muted;
  font-size: 0.82rem;
  line-height: 1.6;
  max-width: 720px;
}

.waw-field {
  display: block;
  max-width: 360px;
  margin: 0.9rem 0 0;
}

.waw-field__label {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin-bottom: 0.35rem;
}

/* A plain <input>, not b-form-input: it carries this component's scope attribute directly, so the
   styling cannot leak (repo CSS rule). */
.waw-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.7rem;
  font-size: 0.9rem;
  color: $pp-text;
  background: $pp-surface;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;

  &:focus {
    outline: none;
    border-color: $pp-brand;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }

  &:disabled {
    background: $pp-surface-sunken;
    color: $pp-text-muted;
  }
}

.waw-field__hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.78rem;
  line-height: 1.55;
  color: $pp-text-subtle;
}

@media (max-width: 767.98px) {
  .waw-choices,
  .waw-choices--three {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
